import Backdrop from "@components/Backdrop"
import { StyledButton } from "@components/buttons/Button"
import LinkButton, {
  Button as LinkButtonContainer,
} from "@components/buttons/LinkButton"
import { Check, CloseAlt } from "@components/icons"
import ImageHelper, {
  Container as ImageHelperContainer,
} from "@components/ImageHelper"
import animation, { cubicBezier } from "@constants/animation"
import breakpoints from "@constants/breakpoints"
import colors from "@constants/colors"
import {
  body14Styles,
  body16BoldStyles,
  smallcapsBold14Styles,
} from "@constants/typography"
import useModalBackgroundClose from "@hooks/useModalBackgroundClose"
import { ToastProduct } from "@redux/types"
import { getProductThumbnailAlt } from "@utils/altText"
import { getProductURL } from "@utils/urls"
import { Link } from "gatsby"
import React from "react"
import styled, { css } from "styled-components"

const openProps = css`
  z-index: 1002;
  opacity: 1;
  transform: translateY(0);
  transition:
    opacity ${animation.quick} ${cubicBezier},
    transform ${animation.medium} ${cubicBezier};
  pointer-events: initial;

  @media ${breakpoints.laptop} {
    transform: translateY(0);
  }
`

// xPosition = Header padding + Button padding + Menu icon length - intrinsic padding
const Container = styled.div<{
  $isOpen?: boolean
  $xPosition?: string
  $yPosition?: string
}>`
  ${({ $isOpen, $xPosition, $yPosition }) => css`
    z-index: -1;
    position: fixed;
    top: 0;
    right: 0;
    width: 100%;
    opacity: 0;
    transform: translateY(-100%);
    transition:
      opacity ${animation.medium} ${cubicBezier},
      transform ${animation.quick} ${cubicBezier};
    will-change: opacity, transform;
    background-color: ${colors.offWhite};
    pointer-events: none;
    cursor: default;

    @media ${breakpoints.laptop} {
      top: ${$yPosition || 0};
      right: ${$xPosition || "calc(1.25 / 12 * 100vw - 38px + 24px - 16px)"};
      transform: translateY(20%);
      width: 376px;
    }

    @media ${breakpoints.largeDesktop} {
      right: ${$xPosition || "calc(0.5 / 12 * 100vw - 38px + 120px - 16px)"};
    }

    ${$isOpen && openProps}
  `}
`

const Diamond = styled.div`
  display: none;

  @media ${breakpoints.laptop} {
    position: absolute;
    top: -6px;
    right: 20px;
    display: initial;
    transform: rotate(-45deg);
    width: 16px;
    height: 16px;
    background-color: ${colors.offWhite};
  }
`

const Header = styled.button`
  position: relative;
  align-items: center;
  justify-content: center;
  display: flex;
  height: 72px;
  width: 100%;
  padding: 0 16px;
  outline: none;
  border: none;
  border-bottom: solid 1px ${colors.dividerLineGray};
  background: none;

  &:focus,
  &:active {
    outline: none;
  }

  @media ${breakpoints.tablet} {
    padding-left: 24px;
  }

  @media ${breakpoints.laptop} {
    padding-left: 24px;
  }
`

const HeaderText = styled.div`
  ${smallcapsBold14Styles}
  flex-grow: 1;
  padding-top: 4px;
  padding-left: 2px;
  text-align: left;
  color: ${colors.mediumDarkGray};
`

const IconWrapper = styled.div`
  width: 24px;
  height: 24px;
`

const CloseIconWrapper = styled.div`
  width: 10.67px;
  height: 10.67px;
  display: flex;
`

const Body = styled.div`
  padding: 26px 28px 48px;

  @media ${breakpoints.laptop} {
    padding: 32px 24px 40px;
  }
`

const Product = styled.div`
  flex-direction: row;
  align-items: center;
  justify-content: center;
  display: flex;
  margin-bottom: 40px;
`

const ImageLink = styled(Link)<any>`
  ${({ to }) => (!to.length ? "pointer-events: none" : "cursor: pointer")};
  align-self: flex-start;

  ${ImageHelperContainer} {
    position: relative;
    overflow: hidden;
    width: 80px;
    height: 80px;
    margin-right: 16px;
  }
`

const Details = styled.div`
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
`

const Name = styled.div`
  ${body16BoldStyles}
  margin-bottom: 16px;
  padding-right: 20px;
`

const Option = styled.div`
  justify-content: flex-start;
  display: flex;
`

const OptionName = styled.div`
  ${body14Styles}
  flex-shrink: 0;
  width: 64px;
  font-weight: 700;
`

const OptionValue = styled.div`
  ${body14Styles}
`

const Buttons = styled.div`
  align-items: center;
  justify-content: space-between;
  display: flex;

  ${StyledButton} {
    width: calc(50% - 10px);
  }

  ${StyledButton}, ${LinkButtonContainer} {
    justify-content: center;
  }

  ${StyledButton}, ${LinkButtonContainer}, span {
    padding: 0;
  }
`

const LinkButtonWrapper = styled.div<{ $disableLink: boolean }>`
  ${({ $disableLink }) => css`
    width: calc(50% - 10px);

    a {
      display: flex;
      pointer-events: ${$disableLink ? "none" : "initial"};
    }

    &:hover {
      cursor: ${$disableLink ? "progress" : "pointer"};
    }
  `}
`

export interface CartToastProps {
  cartCount: number
  checkoutLink: string
  isOpen: boolean
  onClickCart: () => void
  onRequestClose: () => void
  product: null | ToastProduct
  xPosition?: string
  yPosition?: string
}

const CartToast: React.FC<CartToastProps> = ({
  cartCount,
  checkoutLink,
  isOpen,
  onClickCart,
  onRequestClose,
  product,
  xPosition,
  yPosition,
}) => {
  const toastRef: any = React.useRef(null)

  useModalBackgroundClose({
    excludeIds: ["desktopHeader"],
    isOpen,
    modalRef: toastRef,
    onRequestClose,
  })

  const handleOnRequestClose = (e) => {
    e.stopPropagation()
    onRequestClose()
    onClickCart()
  }

  const disableCheckoutLink = !checkoutLink || !checkoutLink.length

  return (
    <>
      <Backdrop $isOpen={isOpen} />
      <Container
        $isOpen={isOpen}
        $xPosition={xPosition}
        $yPosition={yPosition}
        ref={toastRef}
      >
        <Header onClick={onRequestClose}>
          <Diamond />
          <IconWrapper>
            <Check strokeColor={colors.mediumDarkGray} />
          </IconWrapper>
          <HeaderText>Added to Cart!</HeaderText>
          <CloseIconWrapper>
            <CloseAlt strokeColor={colors.mediumDarkGray} />
          </CloseIconWrapper>
        </Header>
        <Body>
          {product && (
            <Product>
              <ImageLink to={getProductURL(product.productSlug)}>
                <ImageHelper
                  alt={getProductThumbnailAlt(product.productName)}
                  src={product.image}
                />
              </ImageLink>
              <Details>
                <Name>{product.productName}</Name>
                {product.customPrint && product.variantDevice ? (
                  <Option>
                    <OptionName>Device:</OptionName>
                    <OptionValue>{product.variantDevice}</OptionValue>
                  </Option>
                ) : (
                  <>
                    <Option>
                      <OptionName>Device:</OptionName>
                      <OptionValue>{product.device}</OptionValue>
                    </Option>
                    <Option>
                      <OptionName>Color:</OptionName>
                      <OptionValue>{product.variantName}</OptionValue>
                    </Option>
                  </>
                )}
              </Details>
            </Product>
          )}
          <Buttons>
            <StyledButton
              $white
              onClick={handleOnRequestClose}
            >{`Cart (${cartCount})`}</StyledButton>
            <LinkButtonWrapper $disableLink={disableCheckoutLink}>
              <LinkButton external noDash text="Checkout" to={checkoutLink} />
            </LinkButtonWrapper>
          </Buttons>
        </Body>
      </Container>
    </>
  )
}

export default CartToast
